/**
 * Ikaros edition filter.
 *
 * WHERE THE CONTROL LIVES
 * Inside Horizon's own filter panel, beside Availability, Price and Cap
 * style - because that is where a customer looks for a filter. Shopify will
 * not let a theme add a group there (only the Search & Discovery app can
 * create a real facet), so this clones Horizon's own panel markup and
 * appends it to each .facets__form. It looks native because it reuses
 * Horizon's classes; it behaves differently because nothing is submitted.
 *
 * There are two facet forms on a collection page - the horizontal one for
 * desktop and the vertical one used by the mobile drawer - so the panel is
 * appended to every form found, and the pills stay in step with each other.
 *
 * The standalone bar above the grid is the fallback. If Horizon's markup
 * ever changes and no form is found, that bar stays visible and the filter
 * still works. When injection succeeds the bar hides itself, so the same
 * control is never on screen twice.
 *
 * HOW FILTERING WORKS
 * Liquid supplies a handle to edition map; each card's product link gives
 * its handle; matching cards are shown and the rest hidden. Horizon's grid,
 * sort and pagination are untouched.
 *
 * The URL carries the choice (?edition=World+Cup) so a filtered view can be
 * shared. History is replaced rather than pushed - filtering is not
 * navigation, and four pills should not cost four Backs.
 *
 * With JavaScript off nothing binds and every product stays visible, which
 * is the right failure: the grid is the product, the filter is a
 * convenience.
 */

(() => {
  const bar = document.querySelector('[data-ikef]');
  const mapEl = document.querySelector('[data-ikef-map]');
  if (!bar || !mapEl) return;

  let map = {};
  try {
    map = JSON.parse(mapEl.textContent);
  } catch (e) {
    console.warn('[ikaros-edition-filter] bad map', e);
    return;
  }

  const none = document.querySelector('[data-ikef-none]');
  const label = bar.dataset.ikefLabel || 'Edition';

  /* Every pill on the page, wherever it was injected. Recomputed on demand
     so panels added later are included. */
  const allPills = () => [...document.querySelectorAll('[data-ikef-pill]')];

  function handleOf(card) {
    const link = card.querySelector('a[href*="/products/"]');
    if (!link) return null;
    const m = link.getAttribute('href').match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  /* Re-read every time: Horizon replaces the grid on sort and on paging, so
     a list captured once would point at detached nodes. */
  function cards() {
    return [...document.querySelectorAll('.product-grid__item, product-card')]
      .filter((el) => !el.closest('[data-ikef]') && !el.closest('.facets__form'));
  }

  function apply(value) {
    let shown = 0;

    cards().forEach((card) => {
      const item = card.closest('.product-grid__item') || card;
      const h = handleOf(card);
      const match = !value || (h && map[h] === value);
      item.hidden = !match;
      if (match) shown += 1;
    });

    allPills().forEach((p) => {
      const mine = p.dataset.ikefPill || '';
      p.setAttribute('aria-pressed', mine === value ? 'true' : 'false');
    });

    if (none) none.hidden = shown !== 0;

    const url = new URL(window.location);
    if (value) {
      url.searchParams.set('edition', value);
    } else {
      url.searchParams.delete('edition');
    }
    window.history.replaceState({}, '', url);
  }

  function bind(el) {
    if (el.dataset.ikefBound) return;
    el.dataset.ikefBound = '1';
    el.addEventListener('click', (e) => {
      e.preventDefault();
      apply(el.dataset.ikefPill || '');
    });
  }

  /* Build a panel that matches Horizon's own, so it reads as one of the
     store's filters rather than something bolted on. type=button matters:
     these sit inside Horizon's form and must never submit it. */
  function panelFor(form) {
    const details = document.createElement('details');
    details.className = 'facets__panel';
    details.open = true;
    details.setAttribute('data-ikef-panel', '');

    const summary = document.createElement('summary');
    summary.className = 'facets__summary';
    const span = document.createElement('span');
    span.className = 'facets__label';
    span.textContent = label;
    summary.appendChild(span);
    details.appendChild(summary);

    const body = document.createElement('div');
    body.className = 'ikef__panelbody';

    [...bar.querySelectorAll('[data-ikef-pill]')].forEach((src) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'ikef__pill';
      b.dataset.ikefPill = src.dataset.ikefPill || '';
      b.setAttribute('aria-pressed', src.getAttribute('aria-pressed') || 'false');
      b.innerHTML = src.innerHTML;
      bind(b);
      body.appendChild(b);
    });

    details.appendChild(body);
    form.appendChild(details);
  }

  function inject() {
    const forms = [...document.querySelectorAll('.facets__form')];
    let added = 0;
    forms.forEach((form) => {
      if (form.querySelector('[data-ikef-panel]')) return;
      panelFor(form);
      added += 1;
    });
    if (forms.length) bar.closest('.ikef').hidden = true;
    return added;
  }

  [...bar.querySelectorAll('[data-ikef-pill]')].forEach(bind);
  inject();

  const initial = new URL(window.location).searchParams.get('edition') || '';
  if (initial) apply(initial);

  /* Horizon re-renders the facet panel and the grid on sort and paging,
     which would drop both the injected panel and the current choice. */
  const origFetch = window.fetch;
  window.fetch = function (...args) {
    const p = origFetch.apply(this, args);
    const active = allPills().find((x) => x.getAttribute('aria-pressed') === 'true');
    const value = active ? active.dataset.ikefPill || '' : '';
    p.then(() => {
      setTimeout(() => {
        inject();
        if (value) apply(value);
      }, 400);
    }).catch(() => {});
    return p;
  };
})();
